import { Dispatch } from 'react'
import { vn } from 'vend-number'
import { ApolloError } from '@apollo/client'
import { getMainTender } from '../../../../../../functions/payments'
import { TRANSACTION_STATUSES } from '../../helpers/constants'
import {
  SwiperErrorMessage,
  SwiperPaymentEvent,
} from '../../hooks/use-swiper-payment-reducer/types'
import { Transaction } from '../../queries/Transaction'
import { loadTransactionSignature } from './loadTransactionSignature'

const isPartialApproved = (transaction: Transaction) => {
  const mainTender = getMainTender(transaction.tenders)

  if (!mainTender) {
    return false
  }

  const amount = mainTender.amount
  const requestAmount = mainTender.providerFields.request_amount
  return (
    mainTender.providerFields.is_partial_approved &&
    vn(amount).gt(0) &&
    vn(requestAmount).gt(amount)
  )
}

export const handleTransaction = async (
  transaction: Transaction,
  dispatch: Dispatch<SwiperPaymentEvent>,
  registerId: string,
  getTransactionErrorMessages: (errorCode: string) => SwiperErrorMessage,
  getSwiperErrorMessage: (error?: ApolloError) => SwiperErrorMessage
) => {
  const tenderStatus = getMainTender(transaction.tenders)?.status
  switch (transaction.status) {
    case TRANSACTION_STATUSES.COMPLETED:
    case TRANSACTION_STATUSES.REFUNDED: {
      if (tenderStatus === TRANSACTION_STATUSES.APPROVED_SIGNATURE) {
        try {
          const signatureSrc = await loadTransactionSignature(
            registerId,
            transaction.registerSalePaymentID
          )
          dispatch({
            type: 'PROMPT_APPROVE_SIGNATURE',
            transaction,
            signatureSrc,
          })
        } catch {
          dispatch({ type: 'PROMPT_PRINT_SIGNATURE', transaction })
        }
        return
      }
      if (
        tenderStatus === TRANSACTION_STATUSES.APPROVED ||
        tenderStatus === TRANSACTION_STATUSES.APPROVED_PIN ||
        tenderStatus === TRANSACTION_STATUSES.REFUNDED
      ) {
        if (isPartialApproved(transaction)) {
          dispatch({
            type: 'PROMPT_PARTIAL_APPROVAL',
            transaction,
          })
          return
        }
        dispatch({ type: 'DONE', transaction })
        return
      }
      dispatch({
        type: 'ERROR',
        errorMessage: getSwiperErrorMessage(), // Without error will return the default unknown error message
      })
      return
    }
    case TRANSACTION_STATUSES.REVERSED: {
      if (tenderStatus === TRANSACTION_STATUSES.REVERSED) {
        dispatch({
          type: 'REVERSED',
          message: getTransactionErrorMessages(TRANSACTION_STATUSES.REVERSED),
        })
        return
      }
      dispatch({
        type: 'ERROR',
        errorMessage: getSwiperErrorMessage(), // Without error will return the default unknown error message
      })
      return
    }
    case TRANSACTION_STATUSES.CANCELLED: {
      if (tenderStatus === TRANSACTION_STATUSES.DECLINED) {
        dispatch({
          type: 'ERROR',
          errorMessage: getTransactionErrorMessages(
            TRANSACTION_STATUSES.DECLINED
          ),
          shouldPrintReceipt: true,
        })
        return
      }
      dispatch({
        type: 'ERROR',
        errorMessage: getTransactionErrorMessages(
          TRANSACTION_STATUSES.CANCELLED
        ),
      })
      return
    }

    case TRANSACTION_STATUSES.CANCELLED_BUSY:
    case TRANSACTION_STATUSES.CANCELLED_BAD_CARD:
    case TRANSACTION_STATUSES.TIMEOUT: {
      dispatch({
        type: 'ERROR',
        errorMessage: getTransactionErrorMessages(transaction.status),
      })
      return
    }

    default: {
      dispatch({
        type: 'ERROR',
        errorMessage: getSwiperErrorMessage(), // Without error will return the default unknown error message
      })
      return
    }
  }
}
